import { TreeSelect, TreeSelectProps } from 'antd'
import React, { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useFetchAccounts } from '../queries/useFetchAccounts'
import { companyAccountsByStatementRowIdSelector } from '../selectors/selectors'
import { Account } from '../types/Account'
import { groupedStatementRowsSelector } from '../../../redux/entities/statementRows/selectors'
import { FinancialStatementReportType } from '../../../types/statementRow/StatementRow'

interface AccountTreeSelectProps extends TreeSelectProps {
  reports?: FinancialStatementReportType[] // Optional: filter by specific reports
}

/**
 * Expands group selections to individual account codes
 * @example expandGroupsToAccounts([3000, 'group-5'], accountsByStatementRowId) => [3000, 3100, 3200, ...]
 */
const expandGroupsToAccounts = (
  sourceIds: (number | string | null)[] | undefined,
  accountsByStatementRowId: Record<string, any[]> | undefined
): (number | null)[] => {
  if (!sourceIds || !accountsByStatementRowId) return []

  const result: (number | null)[] = []

  sourceIds.forEach(id => {
    if (id === null) {
      result.push(null)
    } else if (typeof id === 'string' && id.startsWith('group-')) {
      // Extract statement row ID from "group-X" format
      const statementRowId = id.replace('group-', '')
      const accounts = accountsByStatementRowId[statementRowId] || []
      // Add all account codes from this group
      accounts.forEach(account => result.push(account.code))
    } else {
      // Regular account code
      result.push(typeof id === 'string' ? Number(id) : id)
    }
  })

  return result
}

const AccountTreeSelect: React.FC<AccountTreeSelectProps> = ({ reports, onChange, ...props }) => {
  const { t } = useTranslation()
  const { data: accountsByStatementRowId, isLoading } = useFetchAccounts(companyAccountsByStatementRowIdSelector)
  const groupedStatementRows = useSelector(groupedStatementRowsSelector)

  // Sort statement rows by report type and order
  const treeData = useMemo(() => {
    if (!accountsByStatementRowId || !groupedStatementRows) return []

    // Define report order - use provided reports or default to INCOME_STATEMENT only
    const reportOrder = reports || [FinancialStatementReportType.INCOME_STATEMENT]

    // Flatten and sort all statement rows by id (maintains order from backend)
    const allStatementRows = reportOrder.flatMap(report => {
      const rows = groupedStatementRows[report] || []
      return rows
    })

    // Build tree data in correct order
    return allStatementRows
      .filter(row => accountsByStatementRowId[row.id]) // Only include rows that have accounts
      .map(row => {
        const accounts = accountsByStatementRowId[row.id]
        const translationKey = row.report === 'INCOME_STATEMENT' ? 'incomeStatement' : 'balanceSheet'

        return {
          title: t(`${translationKey}:${row.id}`, row.title || String(row.id)),
          value: `group-${row.id}`,
          selectable: false,
          children: accounts.map((account: Account) => ({
            title: `${account.code} - ${account.name}`,
            value: account.code
          }))
        }
      })
  }, [accountsByStatementRowId, groupedStatementRows, reports, t])

  // Wrap onChange to expand group selections to individual account codes
  const handleChange = useCallback(
    (value: any, labelList: any, extra: any) => {
      if (!onChange) return

      // If value is an array (multiple selection), expand groups
      if (Array.isArray(value)) {
        const expandedValue = expandGroupsToAccounts(value, accountsByStatementRowId)
        onChange(expandedValue, labelList, extra)
      } else {
        // Single selection - pass through as-is
        onChange(value, labelList, extra)
      }
    },
    [onChange, accountsByStatementRowId]
  )

  return (
    <TreeSelect
      loading={isLoading}
      disabled={isLoading}
      // eslint-disable-next-line react/destructuring-assignment
      placeholder={isLoading ? t('global:loading', 'Loading...') : props.placeholder}
      treeData={treeData}
      tagRender={isLoading ? () => <></> : undefined}
      onChange={handleChange}
      {...props}
    />
  )
}

export default AccountTreeSelect
